import axios from 'axios';
import { apiCache, createCacheKey } from '../utils/cache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors for token refresh
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Determine refresh endpoint based on user type
        const userType = localStorage.getItem('userType') || 'internal';
        const refreshEndpoint = userType === 'partner' 
          ? `${API_BASE_URL}/partner/auth/refresh` 
          : `${API_BASE_URL}/auth/refresh`;

        // Attempt to refresh token
        const refreshResponse = await axios.post(refreshEndpoint, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json',
          }
        });

        if (refreshResponse.data.success && refreshResponse.data.data?.token) {
          const newToken = refreshResponse.data.data.token;
          localStorage.setItem('token', newToken);
          
          // Update the authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Process queued requests
          processQueue(null, newToken);
          
          // Retry the original request
          return api(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        
        console.warn('⚠️ Token refresh failed, logging out user');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
        
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For 403 errors (forbidden), don't logout - just reject
    if (error.response?.status === 403) {
      console.warn('⚠️ Access forbidden - insufficient permissions');
      return Promise.reject(error);
    }

    // For other errors, just reject without logout
    return Promise.reject(error);
  }
);

export default api;

// Cached GET request helper
export const cachedGet = async <T = any>(
  endpoint: string, 
  params?: Record<string, any>,
  ttl: number = 5 * 60 * 1000 // 5 minutes default
): Promise<T> => {
  const cacheKey = createCacheKey(endpoint, params);
  
  // Check cache first
  const cached = apiCache.get<T>(cacheKey);
  if (cached) {
    return cached;
  }

  // Make API call
  const response = await api.get<T>(endpoint, { params });
  
  // Store in cache
  apiCache.set(cacheKey, response.data, ttl);
  
  return response.data;
};

// Helper to invalidate cache for specific patterns
export const invalidateCache = (pattern: string) => {
  apiCache.invalidatePattern(pattern);
};

// Helper to clear all cache
export const clearCache = () => {
  apiCache.clear();
};
